import { useCallback, useRef } from 'react';

/**
 * Creates a function that will only execute once, no matter how many times it's called.
 * Useful for tracking events or side effects that should only fire once per component lifecycle.
 *
 * @param callback - The function to execute once
 * @returns A memoized function that will only execute the callback once
 *
 * @example
 * const trackOnce = useCallOnce(() => {
 *   analytics.track('event_fired');
 * });
 *
 * // First call executes the callback
 * trackOnce();
 * // Subsequent calls do nothing
 * trackOnce();
 */
export function useCallOnce<T extends (...args: any[]) => any>(callback: T): T {
  const fired = useRef(false);

  return useCallback(
    (...args: Parameters<T>) => {
      if (fired.current) return;
      fired.current = true;
      return callback(...args);
    },
    [callback],
  ) as T;
}
